import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface BusinessVendorIdentity {
  id: string;
  business_id: string;
  trading_name: string | null;
  legal_form_id: string | null;
  registration_number: string | null;
  vat_number: string | null;
  tax_entity_number: string | null;
  country_code: string | null;
  iban: string | null;
  bic: string | null;
  is_related_party: boolean;
  created_at: string;
  updated_at: string;
  legal_forms: {
    id: string;
    code: string;
    name: string;
    country_code: string;
  } | null;
}

/**
 * The business's vendor identity (legal form, registration / VAT numbers,
 * IBAN / BIC) — a direct read (burn-down list, docs/BACKEND_API.md). There is
 * no write hook: the identity is written only as `vendor_identity` on the
 * business create / update (`useCreateBusiness` / `useUpdateBusiness`, same
 * transaction), which invalidate this key.
 */
export function useBusinessVendorIdentity(businessId?: string) {
  return useQuery({
    queryKey: ['business-vendor-identity', businessId],
    queryFn: async () => {
      if (!businessId) return null;

      const { data, error } = await supabase
        .from('business_vendor_identity')
        .select(`
          *,
          legal_forms (
            id,
            code,
            name,
            country_code
          )
        `)
        .eq('business_id', businessId)
        .maybeSingle();

      if (error) throw error;
      return data as BusinessVendorIdentity | null;
    },
    enabled: !!businessId,
    staleTime: 5 * 60 * 1000,
  });
}
